import React, {useState, useEffect, useRef, useMemo} from 'react';
import {Card, Button, Drawer, H3, H2, Spinner} from '@blueprintjs/core';
import styled from 'styled-components';

import { GOOGLE_MAPS_API_KEY, GOOGLE_TIMEZONE_API_KEY } from '../../../config/vars';
import {
    APIProvider as MapsAPIProvider,
    Map,
    MapControl,
    ControlPosition,
    AdvancedMarker,
    useMap,
    useMapsLibrary,
    useAdvancedMarkerRef
} from '@vis.gl/react-google-maps';

import {AvailableTimezones, AvailableCurrencyCodes, AvailableCountryCodes, OpeningTimes} from "./properties";
import { organisations } from "/src/services/api";
import {SchemaForm} from "/src/components/forms";


const _LocSchema = ({...props}) => {
    return {
        type: "object",
        required: [
            "name",
            "organisation",
            "coordinates",
            "timezone",
            "openingTimes"
        ],
        properties: {
            name: {
                type: "string"
            },
            organisation: {
                oneOf: props.availableOrganisations
                    ? props.availableOrganisations.map(org => ({ const: org.id, title: org.name }))
                    : [{const: ""}]
            },
            timezone: {
                type: "string",
                enum: AvailableTimezones,
            },
            currency: {
                type: "string",
                enum: AvailableCurrencyCodes,
            },
            countryCode: {
                type: "string",
                enum: AvailableCountryCodes,
            },
            coordinates: {
                type: "string",
                pattern: "(?<lat>^[-+]?(?:[1-8]?\\d(?:\\.\\d+)?|90(?:\\.0+)?))\\s*,\\s*(?<lng>[-+]?(?:180(?:\\.0+)?|(?:1[0-7]\\d|[1-9]?\\d)(?:\\.\\d+)?))$"
            },

            openingTimes: {
                type: "array",
                minItems: 7,
                maxItems: 7,
                items: {
                    type: "object",
                    properties: {
                        day: {type: "integer", minimum: 0, maximum: 6},
                        startHour: {type: "integer", minimum: 0, maximum: 23},
                        startMinute: {type: "integer", minimum: 0, maximum: 59},
                        stopHour: {type: "integer", minimum: 0, maximum: 23},
                        stopMinute: {type: "integer", minimum: 0, maximum: 59},
                    }
                }
            },
        }
    }
}

const _UILocSchema = () => ({
    type: "VerticalLayout",
    elements: [
        {
            type: "Group",
            elements: [
                {
                    type: "HorizontalLayout",
                    elements: [
                        {
                            type: "Control",
                            scope: "#/properties/organisation",
                        },
                        {
                            type: "Control",
                            scope: "#/properties/name"
                        },
                    ]
                },
                {
                    type: "HorizontalLayout",
                    elements: [
                        {
                            type: "Control",
                            scope: "#/properties/coordinates",
                            // rule: { effect: "DISABLE", condition: { scope: "#", schema: {} } }
                        },
                        {
                            type: "Control",
                            scope: "#/properties/timezone",
                        },
                        {
                            type: "Control",
                            scope: "#/properties/currency",
                        }
                    ]
                },
            ]
        },
    ]
})

const ContentWrapper = styled.form`
  width: 90%;
  margin: 2em auto;
  overflow: auto;
`;

const MapSection = styled.div`
    display: block;
    margin: 2em auto;
    width: 90%;
    height: 25em;
`

const SearchBox = styled.input`
    margin: 1em;
    width: 50ch;
    font-size: 1.5em;
    border: 5px solid #130a35;
`

const StyledButton = styled(Button)`
    display: block;
    margin: 2em auto;
`

const transformOpeningTimes = (periods) => {
    // Google Maps opening_hours.periods is an array with {open:x, close:x} objects
    // where x = {day, time, hours, minutes} the array does not contain days in which
    // the place is closed
    const opens = periods.reduce((acc, cur) => ({...acc, [cur.open.day]: cur.open}), {});
    const close = periods.reduce((acc, cur) => ({...acc, [cur.close.day]: cur.close}), {});
    return [
        ...Array(7).keys()
    ].reduce((acc, day) => [
        ...acc,
        {
            day: day,
            startHour: opens[day]?.hours ?? 0,
            startMinute: opens[day]?.minutes ?? 0,
            stopHour: close[day]?.hours ?? 23,
            stopMinute: close[day]?.minutes ?? 59,
        }
    ], []);
}

const formatName = (name, address, org_name = undefined) => {
    let ret = org_name?
        name.toLowerCase().replace(org_name.toLowerCase(), '')
        : name.toLowerCase();
    ret = ret? ret.replace(
        /\w\S*/g,
        text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
    ) : address ?? name;
    return ret.replace(/[^0-9a-z ]/gi, '');
}


const PlaceAutocomplete = ({ onPlaceSelect }) => {
    const [placeAutocomplete, setPlaceAutocomplete] = useState(null);
    const inputRef = useRef(null);
    const places = useMapsLibrary("places");
    const [currentListener, setCurrentListener] = useState(null);

    useEffect(() => {
        if (!places || !inputRef.current) return;
        setPlaceAutocomplete(new places.Autocomplete(
            inputRef.current,
            {fields: ["geometry", "name", "formatted_address", "opening_hours"]}
        ));
    }, [places]);

    useEffect(() => {
        if (!!placeAutocomplete) {
            if (currentListener) currentListener.remove();
            setCurrentListener(
                placeAutocomplete.addListener("place_changed", () => {
                    onPlaceSelect(placeAutocomplete.getPlace());
                })
            )
        }
    }, [onPlaceSelect, placeAutocomplete]);

    return (
        <div className="autocomplete-container">
            <SearchBox ref={inputRef} placeholder="Search" />
        </div>
    );
};


const MapHandler = ({ place, marker }) => {
    const map = useMap();

    useEffect(() => {
        if (!map || !place || !marker) return;

        if (place.geometry?.viewport) {
            map.fitBounds(place.geometry?.viewport);
        }

        marker.position = place.geometry?.location;
    }, [map, place, marker]);
    return null;
};

const GeoCode = ({ latlng, shouldGeocode, after }) => {
    const [_latlng, set_latlng] = useState(latlng);
    const geocodingLib = useMapsLibrary('geocoding');
    const geocoder = useMemo(() => geocodingLib && new geocodingLib.Geocoder(), [geocodingLib]);

    useEffect(() => {
        if (!geocoder) return;
        console.log("Loaded geocode...");
    }, [geocoder]);

    useEffect(() => {
        if (!!geocoder && !!_latlng && shouldGeocode) {
            const latlngSplit = _latlng.split(",");
            geocoder.geocode({
                location: {lat: parseFloat(latlngSplit[0]), lng: parseFloat(latlngSplit[1])},
            }).then((result) => {
                if (result.results?.length) after(result.results[0]);
            }).catch(error => {
                console.log(error);
            })
        }
    }, [_latlng]);

    if (latlng !==  _latlng) set_latlng(latlng);

    return <></>;
};


const CreateLocation = ({ isOpen, onClose, onSave, preSelectedOrg }) => {
    const [loading, setLoading] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState(preSelectedOrg);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [shouldGeocode, setShouldGeocode] = useState(false);
    const [markerRef, marker] = useAdvancedMarkerRef();
    const [orgMap, setOrgMap] = useState({});
    const [locSchema, setLocSchema] = useState(_LocSchema({}));
    const [uiLocSchema, setUiLocSchema] = useState(_UILocSchema(locSchema));
    const [hasCoordinatesError, setHasCoordinatesError] = useState(false);
    const [hasFormError, setHasFormError] = useState(false);
    const [hasOtherError, setHasOtherError] = useState(false);


    const [formData, setFormData] = useState({
        organisation: selectedOrg?.id,
        name: "",
        coordinates: "",
        timezone: "",
        currency: undefined,
        openingTimes: [
            ...Array(7).keys()
        ].reduce((acc, day) => [...acc, {day: day, startHour: 0, startMinute: 0, stopHour: 23, stopMinute: 59}], [])
    });

    useEffect(() => {
        if (preSelectedOrg) {
            setSelectedOrg(preSelectedOrg);
            setFormData(prev => ({...prev, organisation: preSelectedOrg.id}));
        }
    }, [preSelectedOrg]);

    useEffect(() => {
        // onComponentDidMount
        setLoading(true);
        organisations.list().then((availableOrganisations) => {
            availableOrganisations.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase()? 1 : -1);
            const _locSchema = _LocSchema({availableOrganisations});
            setOrgMap(availableOrganisations.reduce((map, org ) => ({...map, [org.id]: org}), {}));
            setLocSchema(_locSchema);
            setUiLocSchema(_UILocSchema(_locSchema));
            console.log("Loaded available organisations");
        }).catch(error => {
            console.log("Failed to load: " + error.message);
        }).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (isOpen && !hasCoordinatesError) {
            console.log("Fetching timezone for ", formData.coordinates);
            fetch(
                `https://maps.googleapis.com/maps/api/timezone/json?location=${formData.coordinates}&timestamp=${Math.round(+new Date()/1000)}&key=${GOOGLE_TIMEZONE_API_KEY}`
            ).then( res => res.json()).then(data => {
                setFormData(prev => ({...prev, timezone: data.timeZoneId}));
            })
        } else if (isOpen) {
            console.log("Coordinates changed with error")
        }
    }, [formData.coordinates, hasCoordinatesError])

    return (
        <Drawer
            title={`Creating a location ${selectedOrg? 'for ' + selectedOrg.name : ''}`}
            isOpen={isOpen}
            onClose={() => (
                onClose({...formData})
            )}
            size='75%'
        >
            {loading
            ? <Spinner />
            : <ContentWrapper>
                <H2>Find a location to create</H2>
                <MapsAPIProvider
                    apiKey={GOOGLE_MAPS_API_KEY}
                    onLoad={() => console.log('Maps API has loaded.')}
                >
                    <MapSection>
                        <Map
                            mapId="create-location"
                            defaultZoom={15}
                            defaultCenter={{ lat: 51.5266062, lng: -0.0817229 }}
                            mapTypeControl={false}
                        >
                            <AdvancedMarker ref={markerRef} position={null} />
                        </Map>
                        <MapControl position={ControlPosition.LEFT_TOP}>
                            <div className="autocomplete-control">
                                <PlaceAutocomplete onPlaceSelect={(place) => {
                                    console.log("Selected place", place);
                                    const data = {
                                        ...formData,
                                        name: formatName(place?.name ?? "", place?.formatted_address ?? "", selectedOrg?.name),
                                        coordinates: `${place.geometry.location.lat()},${place.geometry.location.lng()}`,
                                    }
                                    if (!!place.opening_hours?.periods?.length)
                                        data.openingTimes = transformOpeningTimes(place.opening_hours.periods);
                                    setShouldGeocode(false);
                                    setSelectedPlace(place);
                                    setFormData(data);
                                }} />
                            </div>
                        </MapControl>
                        <MapHandler place={selectedPlace} marker={marker} />
                        <GeoCode
                            latlng={formData.coordinates}
                            shouldGeocode={shouldGeocode}
                            after={(place => {
                                console.log("Geocoded place", place);
                                setShouldGeocode(false);
                                setSelectedPlace(place);
                                setFormData(prev => ({
                                    ...prev,
                                    name: formatName(place?.name ?? "", place?.formatted_address ?? "", selectedOrg?.name),
                                }));
                            })}
                        />
                    </MapSection>
                </MapsAPIProvider>
                <H3>And adjust details</H3>
                <SchemaForm
                    schema={locSchema}
                    uischema={uiLocSchema}
                    data={formData}
                    onChange={({ data, errors }) => {
                        const _hasCoordinatesError = errors.some(e => '/coordinates' in e);
                        setHasCoordinatesError(_hasCoordinatesError);
                        setHasFormError(!!errors.length);
                        setShouldGeocode(data.coordinates !== formData.coordinates && !_hasCoordinatesError);
                        setSelectedOrg(orgMap[data.organisation]);
                        setFormData(data);
                    }}
                />
                <Card>
                    <OpeningTimes
                        values={formData.openingTimes}
                        editing={true}
                        onChange={(data, errors) => {
                            setHasOtherError(errors);
                            setFormData({...formData, openingTimes: data});
                        }}
                    />
                </Card>
                <StyledButton
                    text="Create"
                    intent="primary"
                    fill={true}
                    disabled={hasFormError || hasOtherError}
                    onClick={() => {
                        onSave({...formData}, selectedOrg);
                        onClose({...formData});
                    }}
                />
            </ContentWrapper>
        }</Drawer>
    )
}

export { CreateLocation };
